import { useState, useRef, useEffect, useCallback } from 'react';
import airportService from '../../services/airportService';

const PAGE_LIMIT = 50;

const AirportDropdown = ({
  value,
  onChange,
  placeholder = 'Select Airport',
  error = '',
  name = 'airport',
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [airports, setAirports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadingSelected, setLoadingSelected] = useState(false);
  const [selectedAirport, setSelectedAirport] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  const loadAirports = useCallback(async (pageNum = 1, search = '', replace = true) => {
    try {
      if (replace) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const response = await airportService.getAirportList(search, pageNum, PAGE_LIMIT);
      const airportData = response.data || [];
      const paginationInfo = response.pagination || {};

      if (replace) {
        setAirports(airportData);
      } else {
        setAirports((prev) => [...prev, ...airportData]);
      }

      setHasMore(paginationInfo.hasMore || false);
      setPage(pageNum);
    } catch {
      if (replace) {
        setAirports([]);
      }
      setHasMore(false);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    if (!value) {
      setSelectedAirport(null);
      return undefined;
    }

    let cancelled = false;

    const resolveSelectedAirport = async () => {
      try {
        setLoadingSelected(true);
        const response = await airportService.getAirportsByIds([value]);
        const fetched = response?.data?.[0];
        if (!cancelled) {
          setSelectedAirport(fetched || null);
        }
      } catch {
        if (!cancelled) {
          setSelectedAirport(null);
        }
      } finally {
        if (!cancelled) {
          setLoadingSelected(false);
        }
      }
    };

    resolveSelectedAirport();
    return () => {
      cancelled = true;
    };
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;

    const timer = setTimeout(() => {
      if (searchTerm.length >= 2 || searchTerm === '') {
        loadAirports(1, searchTerm, true);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, isOpen, loadAirports]);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  const handleSelect = (airport) => {
    setSelectedAirport(airport);
    onChange({ target: { name, value: airport.id } });
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setSelectedAirport(null);
    onChange({ target: { name, value: '' } });
    setSearchTerm('');
  };

  const handleDropdownClick = () => {
    if (disabled || loadingSelected) return;

    const nextOpen = !isOpen;
    setIsOpen(nextOpen);

    if (nextOpen) {
      setSearchTerm('');
      loadAirports(1, '', true);
    }
  };

  const handleLoadMore = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!loadingMore && hasMore) {
      loadAirports(page + 1, searchTerm, false);
    }
  };

  const inputClass =
    'w-full h-[34px] py-[6px] px-3 text-sm leading-[1.42857143] text-[#555] bg-white border border-[#ccc] rounded shadow-[inset_0_1px_1px_rgba(0,0,0,0.075)] transition-[border-color_box-shadow] duration-150 ease-in-out placeholder:text-[#999] focus:outline-none focus:border-[#66afe9] focus:shadow-[inset_0_1px_1px_rgba(0,0,0,0.075),0_0_8px_rgba(102,175,233,0.6)]';

  const displayLabel = loadingSelected
    ? 'Loading airport...'
    : selectedAirport
      ? `${selectedAirport.airport_name}, ${selectedAirport.country}`
      : placeholder;

  const showSearchHint = searchTerm.length === 1;
  const showResults = !showSearchHint;

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        className={`${inputClass} ${disabled ? 'opacity-60 cursor-not-allowed bg-gray-100' : 'cursor-pointer'} flex items-center gap-2 min-w-0`}
        onClick={handleDropdownClick}
      >
        <span
          className={`flex-1 min-w-0 truncate ${selectedAirport ? 'text-[#555]' : 'text-[#999]'}`}
          title={selectedAirport ? displayLabel : undefined}
        >
          {displayLabel}
        </span>
        {selectedAirport && (
          <span className="shrink-0 text-[#555] font-medium">{selectedAirport.iata}</span>
        )}
        <div className="shrink-0 flex items-center gap-1">
          {selectedAirport && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="text-[#999] hover:text-[#555] bg-transparent border-none cursor-pointer p-1 transition-colors leading-none"
              title="Clear selection"
            >
              <i className="fas fa-times text-sm"></i>
            </button>
          )}
          <span className="pointer-events-none text-[#555] w-4 flex justify-center">
            <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'} text-xs`}></i>
          </span>
        </div>
      </div>

      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-[#ccc] rounded shadow-lg max-h-[300px] overflow-hidden">
          <div className="p-2 border-b border-[#eee]">
            <input
              ref={searchInputRef}
              type="text"
              className="w-full px-3 py-2 text-sm border border-[#ccc] rounded focus:outline-none focus:border-[#66afe9]"
              placeholder="Search airports..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value || '')}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div className="overflow-y-auto max-h-[250px]">
            {showSearchHint ? (
              <div className="px-3 py-2 text-sm text-[#999] text-center">
                Type at least 2 characters to search
              </div>
            ) : loading ? (
              <div className="px-3 py-2 text-sm text-[#999] text-center">
                <i className="fas fa-spinner fa-spin mr-2"></i>
                Searching airports...
              </div>
            ) : airports.length === 0 ? (
              <div className="px-3 py-2 text-sm text-[#999] text-center">
                No airports found
              </div>
            ) : (
              <>
                {airports.map((airport) => (
                  <div
                    key={airport.id}
                    className="px-3 py-2 cursor-pointer hover:bg-[#f5f5f5] flex justify-between items-center text-sm border-b border-[#f0f0f0] last:border-b-0"
                    onClick={() => handleSelect(airport)}
                  >
                    <span className="text-[#555]">
                      {airport.airport_name}, {airport.country}
                    </span>
                    <span className="text-[#555] font-medium ml-2">{airport.iata}</span>
                  </div>
                ))}
                {hasMore && (
                  <button
                    type="button"
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    className="w-full px-3 py-2 text-sm text-[#555] hover:bg-[#f5f5f5] border-t border-[#eee] disabled:opacity-50"
                  >
                    {loadingMore ? (
                      <>
                        <i className="fas fa-spinner fa-spin mr-2"></i>
                        Loading more...
                      </>
                    ) : (
                      'Load more airports'
                    )}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {error && <span className="text-[#a94442] block mt-[5px] text-sm">{error}</span>}
    </div>
  );
};

export default AirportDropdown;
